import * as React from "react";
import CommunicationNoteDialog, {
    CommunicationNotesHistoryProps,
} from "../CommunicationNoteDialog/CommunicationNoteDialog";
import {
    IOutboundSms,
    MessageRecord,
    OutboundEmailStatus,
} from "intake_shared/src/types";
import { toTitleCase } from "../../../../../utils/utils";
import Skeleton from "../../../bits/Skeleton/Skeleton";
import Table from "../../../bits/Table/Table";
import EmptyListState from "../../../bits/EmptyListState/EmptyListState";
import Alert from "../../../bits/Alert/Alert";
import CommunicationPhoneLink, {
    getRingCentralUrl,
} from "../CommunicationPhoneLink/CommunicationPhoneLink";
import DirectionIcon from "../DirectionIcon/DirectionIcon";
import formatCommunicationHistoryTimestamp from "../formatCommunicationHistoryTimestamp";
import styles from "./SmsHistory.module.scss";

export type SmsHistoryRecord = MessageRecord | IOutboundSms;

interface SmsHistoryProps extends CommunicationNotesHistoryProps {
    messages: SmsHistoryRecord[];
    smsLoading: boolean;
    smsError: boolean;
    clientPhone: string;
}

interface TimestampValue {
    toDate: () => Date;
}

const LEGACY_DUPLICATE_WINDOW_MS = 60 * 1000;

function isRingCentralMessage(message: SmsHistoryRecord): message is MessageRecord {
    return "direction" in message;
}

function toDate(value: string | Date | TimestampValue | undefined): Date | null {
    if (!value) {
        return null;
    }
    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
    }
    const date =
        typeof value === "object" && "toDate" in value
            ? value.toDate()
            : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
}

function getSmsDate(message: SmsHistoryRecord): Date | null {
    if (isRingCentralMessage(message)) {
        return toDate(message.creationTime || message.lastModifiedTime);
    }
    return toDate(
        (message.updated_at ?? message.created_at) as Date | TimestampValue
    );
}

function normalizeComparablePhone(phoneNumber?: string): string {
    return phoneNumber?.replace(/\D/g, "") ?? "";
}

function getRingCentralRecipient(message: MessageRecord): string {
    if (typeof message.to === "string") {
        return message.to;
    }
    return message.to?.[0]?.phoneNumber ?? "";
}

function isLegacyDuplicate(
    outboundSms: IOutboundSms,
    ringCentralMessage: MessageRecord
): boolean {
    if (
        outboundSms.status?.toLowerCase() !== OutboundEmailStatus.SENT ||
        ringCentralMessage.direction?.toLowerCase() !== "outbound" ||
        !outboundSms.text ||
        outboundSms.text.trim() !== ringCentralMessage.subject?.trim()
    ) {
        return false;
    }

    const outboundRecipient = normalizeComparablePhone(outboundSms.to);
    const ringCentralRecipient = normalizeComparablePhone(
        getRingCentralRecipient(ringCentralMessage)
    );
    const outboundSentAt = getSmsDate(outboundSms)?.getTime();
    const ringCentralSentAt = getSmsDate(ringCentralMessage)?.getTime();

    return Boolean(
        outboundRecipient &&
            outboundRecipient === ringCentralRecipient &&
            outboundSentAt !== undefined &&
            ringCentralSentAt !== undefined &&
            Math.abs(outboundSentAt - ringCentralSentAt) <=
                LEGACY_DUPLICATE_WINDOW_MS
    );
}

export function mergeSmsHistory(
    ringCentralMessages: MessageRecord[] = [],
    outboundSms: IOutboundSms[] = []
): SmsHistoryRecord[] {
    const ringCentralMessageIds = new Set(
        ringCentralMessages.map((message) => String(message.id))
    );
    const uniqueOutboundSms = outboundSms.filter((message) => {
        if (
            message.ringcentral_generated_message_id &&
            ringCentralMessageIds.has(message.ringcentral_generated_message_id)
        ) {
            return false;
        }
        return !ringCentralMessages.some((ringCentralMessage) =>
            isLegacyDuplicate(message, ringCentralMessage)
        );
    });

    return [...ringCentralMessages, ...uniqueOutboundSms].sort((left, right) => {
        const leftTime = getSmsDate(left)?.getTime() ?? 0;
        const rightTime = getSmsDate(right)?.getTime() ?? 0;
        return rightTime - leftTime;
    });
}

export function getSmsSender(message: SmsHistoryRecord): string {
    if (!isRingCentralMessage(message)) {
        return message.from ?? "Unknown";
    }
    if (typeof message.from === "string") {
        return message.from;
    }
    return message.from?.phoneNumber || message.from?.name || "Purged";
}

export function getSmsRecipient(message: SmsHistoryRecord): string {
    if (!isRingCentralMessage(message)) {
        return message.to ?? "Unknown";
    }
    if (typeof message.to === "string") {
        return message.to;
    }
    const recipients = message.to
        ?.map((recipient) => recipient.phoneNumber)
        .filter(Boolean);
    return recipients?.length ? recipients.join(", ") : "Purged";
}

function getSmsDirection(message: SmsHistoryRecord): string {
    return isRingCentralMessage(message) ? message.direction : "Outbound";
}

export default function SmsHistory({
    messages,
    smsLoading,
    smsError,
    clientPhone,
    notes = [],
    noteActions,
}: SmsHistoryProps) {
    const notesByRecord = React.useMemo(() => {
        const grouped = new Map<string, typeof notes>();
        for (const note of notes) {
            if (note.channel !== "sms") continue;
            const recordNotes = grouped.get(note.ringcentral_record_id) ?? [];
            recordNotes.push(note);
            grouped.set(note.ringcentral_record_id, recordNotes);
        }
        return grouped;
    }, [notes]);
    const columns = React.useMemo(() => {
        return [
            {
                Header: "",
                id: "sent-on",
                accessor: (row: SmsHistoryRecord) => {
                    const sentAt = getSmsDate(row);
                    return (
                        <p>
                            <DirectionIcon direction={getSmsDirection(row)} />
                            {formatCommunicationHistoryTimestamp(sentAt)}
                        </p>
                    );
                },
            },
            {
                Header: "From",
                accessor: (row: SmsHistoryRecord) => (
                    <CommunicationPhoneLink
                        value={getSmsSender(row)}
                        destination="new-sms"
                    />
                ),
            },
            {
                Header: "To",
                accessor: (row: SmsHistoryRecord) => (
                    <CommunicationPhoneLink
                        value={getSmsRecipient(row)}
                        destination="new-sms"
                    />
                ),
            },

            {
                Header: "Content",
                accessor: (row: SmsHistoryRecord) => (
                    <p className={styles.messageContent}>
                        {(isRingCentralMessage(row) ? row.subject : row.text) ||
                            "No message content"}
                    </p>
                ),
            },
            {
                Header: "Message Status",
                accessor: (row: SmsHistoryRecord) => (
                    <p>
                        {(isRingCentralMessage(row) ? row.messageStatus : null) ||
                            toTitleCase(row.status || "Unknown")}
                    </p>
                ),
            },
            {
                Header: "Read Status",
                accessor: (row: SmsHistoryRecord) => (
                    <p>
                        {isRingCentralMessage(row)
                            ? row.readStatus
                            : "Not available"}
                    </p>
                ),
            },
            {
                Header: "Notes",
                id: "notes",
                accessor: (row: SmsHistoryRecord) => {
                    const recordId = isRingCentralMessage(row)
                        ? row.id == null
                            ? undefined
                            : String(row.id)
                        : row.ringcentral_generated_message_id;
                    return recordId && noteActions ? (
                        <CommunicationNoteDialog
                            key={recordId}
                            channel="sms"
                            recordId={recordId}
                            notes={notesByRecord.get(recordId) ?? []}
                            actions={noteActions}
                        />
                    ) : null;
                },
            },
        ];
    }, [noteActions, notesByRecord]);
    const smsHistoryUrl = getRingCentralUrl(clientPhone, "sms-history");

    return (
        <div>
            {smsLoading ? (
                <Skeleton count={3} />
            ) : smsError && messages.length === 0 ? (
                <Alert type="warning">
                    SMS history could not be loaded. Please try again.
                </Alert>
            ) : messages.length === 0 ? (
                <EmptyListState
                    emptyMessage="No Messages Yet"
                    stateHelperText="There are no SMS messages to display. Messages will appear here once communication begins."
                />
            ) : (
                <>
                    {smsError ? (
                        <Alert type="warning">
                            Some SMS history could not be loaded. Please try again.
                        </Alert>
                    ) : null}
                    <Table
                        columns={columns}
                        data={messages}
                        allowSelect={false}
                        handleRowClick={
                            smsHistoryUrl
                                ? () =>
                                      window.open(
                                          smsHistoryUrl,
                                          "_blank",
                                          "noopener,noreferrer"
                                      )
                                : undefined
                        }
                    />
                </>
            )}
        </div>
    );
}
